import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, 
  Users, 
  Shield, 
  Briefcase, 
  MessageCircle,
  Globe,
  CheckCircle,
  Star
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = 'https://customer-assets.emergentagent.com/job_visa-community/artifacts/vrwzubc4_Argo-Atlantic-logo.png';
const HERO_IMAGE = 'https://images.unsplash.com/photo-1768069794857-9306ac167c6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjaXR5c2NhcGUlMjBza3lsaW5lJTIwaGlnaCUyMGVuZCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3NzIyMjA0Njl8MA&ixlib=rb-4.1.0&q=85';
const COMMUNITY_IMAGE = 'https://images.unsplash.com/photo-1758518730523-c9f6336ebdae?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxkaXZlcnNlJTIwcHJvZmVzc2lvbmFscyUyMG1lZXRpbmclMjBtb2Rlcm4lMjBvZmZpY2V8ZW58MHx8fHwxNzcyMjIwNDY4fDA&ixlib=rb-4.1.0&q=85';

const defaultStats = [
  { value: '10K+', label: 'Community Members' },
  { value: '500+', label: 'Expert Providers' },
  { value: '50+', label: 'Countries Covered' },
  { value: '98%', label: 'Satisfaction Rate' }
];

const LandingPage = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(defaultStats);

  useEffect(() => {
    axios.get(`${API}/marketing/facts`).then(res => {
      if (res.data.facts?.length > 0) setStats(res.data.facts);
    }).catch(() => {});
  }, []);

  const features = [
    {
      icon: Users,
      title: 'Community Support',
      description: 'Connect with others on similar immigration journeys. Share experiences and get advice.'
    },
    {
      icon: Shield,
      title: 'Verified Experts',
      description: 'Access trusted lawyers and brokers vetted for their expertise and track record.'
    },
    {
      icon: Briefcase,
      title: 'Professional Services',
      description: 'Book consultations and services from immigration professionals.'
    },
    {
      icon: MessageCircle,
      title: 'Private Consultations',
      description: 'Secure, private chat rooms with your service providers.'
    }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <img 
            src={HERO_IMAGE} 
            alt="City skyline" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/90 to-background" />
          <div className="absolute inset-0 gradient-dark-glow" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-32 md:py-40">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content - spans 7 cols */}
            <div className="lg:col-span-7 space-y-8">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 animate-fade-in">
                <Globe className="h-4 w-4 text-primary" />
                <span className="text-sm text-primary font-medium">Your Immigration Journey Starts Here</span>
              </div>
              
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight animate-slide-up">
                Navigate Your
                <span className="block text-gradient-rose">Immigration Pathway</span>
                With Confidence
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-xl animate-slide-up stagger-1">
                Join a trusted community of immigration seekers, expert lawyers, and experienced brokers. 
                Get personalized guidance every step of the way.
              </p>

              <div className="flex flex-wrap gap-4 animate-slide-up stagger-2">
                {user ? (
                  <Link to="/dashboard">
                    <Button size="lg" className="btn-primary h-12 px-8" data-testid="go-dashboard-btn">
                      Go to Dashboard
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/register">
                      <Button size="lg" className="btn-primary h-12 px-8" data-testid="get-started-btn">
                        Get Started Free
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </Button>
                    </Link>
                    <Link to="/login">
                      <Button size="lg" variant="outline" className="btn-outline h-12 px-8" data-testid="sign-in-btn">
                        Sign In
                      </Button>
                    </Link>
                  </>
                )}
              </div>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 pt-4 animate-fade-in stagger-3">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-10 h-10 rounded-full bg-primary/20 border-2 border-background flex items-center justify-center">
                      <span className="text-xs font-medium">{String.fromCharCode(64 + i)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="h-4 w-4 fill-primary text-primary" />
                  ))}
                  <span className="text-sm text-muted-foreground ml-2">4.9/5 from 2000+ reviews</span>
                </div>
              </div>
            </div>

            {/* Right Content - Logo showcase - spans 5 cols */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end animate-slide-up stagger-2">
              <div className="relative">
                <div className="absolute inset-0 rounded-full bg-primary/20 blur-3xl scale-75" />
                <img 
                  src={LOGO_URL} 
                  alt="Argo Atlantic" 
                  className="relative w-64 h-64 md:w-80 md:h-80 object-contain animate-pulse-glow"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 border-y border-border/40 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animate-slide-up" style={{ animationDelay: `${index * 0.1}s` }}>
                <div className="text-3xl md:text-4xl font-serif font-bold text-gradient-rose">{stat.value}</div>
                <div className="text-sm text-muted-foreground mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">Everything You Need</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From community support to professional services, we provide all the tools for your immigration journey.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="card-default p-8 hover:card-active transition-all duration-300 animate-slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Section */}
      <section className="py-24 md:py-32 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <img 
                src={COMMUNITY_IMAGE} 
                alt="Community" 
                className="rounded-2xl shadow-2xl"
              />
            </div>
            <div className="lg:col-span-7 space-y-6">
              <h2 className="font-serif text-3xl md:text-4xl font-bold">
                Join a Thriving Community
              </h2>
              <p className="text-lg text-muted-foreground">
                Connect with thousands of people who share your immigration goals. Get support, share experiences, 
                and learn from those who've successfully navigated their pathways.
              </p>
              <ul className="space-y-4">
                {[
                  'Anonymous posting for sensitive topics',
                  'Curated groups by visa type and destination',
                  'Expert-moderated discussion boards',
                  'Real-time chat with community members'
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              {!user && (
                <Link to="/register">
                  <Button className="btn-primary mt-4" data-testid="join-community-btn">
                    Join the Community
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            Ready to Start Your Journey?
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Join Argo Atlantic today and take the first step towards your immigration goals.
          </p>
          {!user && (
            <Link to="/register">
              <Button size="lg" className="btn-primary h-14 px-10 text-lg" data-testid="cta-get-started-btn">
                Get Started Free
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/40 py-12">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="Argo Atlantic" className="h-8 w-8" />
              <span className="font-serif font-semibold">Argo Atlantic</span>
            </div>
            <p className="text-sm text-muted-foreground">
              © {new Date().getFullYear()} Argo Atlantic. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
